import { ActionPlanAction } from "src/api/client-api/ActionPlan/ActionPlanAction";
import { formatDifference } from "../../../EditProperties/ImpactSection/ImpactComparisonTables/Common/ImpactComparison";
import { MultiImpactTable } from "./formatComparisonTable";
import { SingleImpactTableList } from "./formatSimpleComparisonTable";

type FormatImpactOptions = {
    currentActionPlanAction: ActionPlanAction | undefined;
    scoreId: number;
    multiImpactTable: MultiImpactTable | null;
    simpleImpactTable: SingleImpactTableList | null;
};
export const formatImpactSummary = (options: FormatImpactOptions): Summary => {
    const { scoreId, multiImpactTable, simpleImpactTable, currentActionPlanAction } = options;

    const totalSummaryValues: SummaryValues[] = [];
    const simpleSummary =
        simpleImpactTable?.tableData.reduce((acc, value) => {
            const row = value.rows.find((row) => row.actionId === currentActionPlanAction?.actionId);
            // Summary is about our current action if displayed
            if (row === undefined) {
                return acc;
            }

            const label = value.property.name || value.property.title;
            const score = row.impactScores?.[scoreId];
            const unit = value.property.unit?.abbreviation ?? "";

            acc.push({
                label,
                score,
                type: "simple",
                unit,
                value: formatDifference({
                    start: row.startElement,
                    end: row.endElement,
                    change: row.elementChange,
                    unit: row.propertyUnit,
                    fieldType: row.fieldType,
                }),
            });

            return acc;
        }, totalSummaryValues) ?? totalSummaryValues;

    const subpropertyScoreMapping: Record<string, number> = {};

    const multiSummaryMapping =
        multiImpactTable?.tableData.reduce((acc, value) => {
            value.extendedImpactData.forEach((row) => {
                if (currentActionPlanAction?.actionId !== row.actionId) return acc;

                const label = row.propertyLabel;
                const score = row.impactScores?.[scoreId];
                if (score === null || score === undefined) {
                    return acc;
                }

                if (label in acc) {
                    acc[label] += score;
                } else {
                    acc[label] = score;
                }
            });
            return acc;
        }, subpropertyScoreMapping) ?? subpropertyScoreMapping;

    const multiSummary: SummaryValues[] = Object.entries(multiSummaryMapping).map(([multiLabel, multiScore]) => {
        return {
            label: multiLabel,
            score: multiScore,
            type: "multivalue",
        };
    });

    const allSummaries = [...multiSummary, ...simpleSummary];
    const total = allSummaries.reduce((acc, v) => acc + (v?.score ?? 0), 0);

    const impactToDisplay = allSummaries.filter((item) => {
        if (item.score === null || item.score === undefined) return false;

        return true;
    });

    return {
        summary: impactToDisplay,
        total,
    };
};

export type Summary = {
    total: number;
    summary: SummaryValues[];
};

export type SummaryValues = {
    label: string;
    score: number | null | undefined;
} & SummaryExtraValue;

type SummaryExtraValue =
    | {
          type: "multivalue";
      }
    | {
          type: "simple";
          value: number | null;
          unit: string;
      };
